import { useMutation, useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { SubscriptionProduct } from '@shared/schema';

interface CheckoutRequest {
  productId: number;
  quantity?: number;
  giftCardAmount?: number;
}

interface CheckoutResponse {
  success: boolean;
  sessionId: string;
  url: string;
}

interface SubscriptionProductsResponse {
  products: SubscriptionProduct[];
}

export function useSubscriptionProducts(category?: string) {
  const url = category 
    ? `/api/subscription-products?category=${encodeURIComponent(category)}` 
    : '/api/subscription-products';
    
  return useQuery<SubscriptionProductsResponse>({
    queryKey: [url],
  });
}

export function useSubscriptionProduct(id: number) {
  return useQuery<{ product: SubscriptionProduct }>({
    queryKey: [`/api/subscription-products/${id}`],
    enabled: !!id,
  });
}

export function useSubscriptionProductCheckout() {
  const { toast } = useToast();
  
  return useMutation<CheckoutResponse, Error, CheckoutRequest>({
    mutationFn: async (request: CheckoutRequest) => {
      const response = await apiRequest('POST', '/api/subscription-products/checkout', request);
      const data = await response.json();
      
      if (!data.success || !data.url) {
        throw new Error(data.error || 'Failed to create checkout session');
      }
      
      return data;
    },
    onSuccess: (data) => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pending_subscription_checkout', data.sessionId);
      }
      
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error('Checkout failed:', error);
      toast({
        title: 'Checkout Failed',
        description: error.message || 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
